import { checkForRemoteChanges, MIN_CHECK_GAP } from '$lib/services/backends/refresh';
import { isDraftModified } from '$lib/services/contents/draft';
import {
  cancelBackup,
  deleteBackup,
  getBackup,
  getBackupSlug,
} from '$lib/services/contents/draft/backup';
import { createDraft } from '$lib/services/contents/draft/create';
import { compareWithStore } from '$lib/services/contents/draft/save/conflict';
import { isWorkflowDraft } from '$lib/services/workflow';

/**
 * @import { EntryDraftState } from '$lib/services/contents/draft/state.svelte';
 * @import { Entry, EntryDraft } from '$lib/types/private';
 */

/**
 * Replace the draft in the editor with a fresh one made from the given version of its entry — the
 * one now on the branch, after someone else has changed it. Whatever the user had typed is dropped,
 * along with the backup made of it; otherwise the new draft would offer to restore the edits that
 * were just given up. The caller asks the user first when there is anything to lose.
 * @param {object} args Arguments.
 * @param {EntryDraftState} args.entryDraft Entry draft state of the editor.
 * @param {Entry} args.entry Entry to make the new draft from.
 * @returns {Promise<EntryDraft>} The new draft.
 */
export const reloadDraft = async ({ entryDraft, entry }) => {
  const draft = /** @type {EntryDraft} */ (entryDraft.current);
  const { collectionName, collection, collectionFile, expanderStates } = draft;

  // A backup scheduled for the edits being dropped would otherwise bring them back
  cancelBackup();
  await deleteBackup(collectionName, getBackupSlug(draft));

  return createDraft({
    entryDraft,
    collection,
    collectionFile,
    originalEntry: entry,
    // Keep the fields expanded or collapsed as the user had them
    expanderStates,
  });
};

/**
 * Check if the given draft is still the one in the editor, and the user hasn’t touched it yet.
 * @param {EntryDraftState} entryDraft Entry draft state of the editor.
 * @param {EntryDraft} draft Draft opened in the editor.
 * @returns {boolean} Result.
 */
const isDraftUntouched = (entryDraft, draft) =>
  entryDraft.current === draft && !draft.interacted && !isDraftModified(draft);

/**
 * Bring a draft that has just been opened for an existing entry up to date with the repository, so
 * the editor shows the entry as it is rather than as it was when the site data was loaded. The
 * check is made in passing, so an entry opened right after another costs nothing. If the entry has
 * been changed in the meantime and the user hasn’t touched the draft yet, it’s quietly made again
 * from the new version. Otherwise — the user has started editing, has a backup to be asked about,
 * or the entry is gone — the notice in the editor takes over, so nothing is lost.
 * @param {EntryDraftState} entryDraft Entry draft state of the editor.
 * @returns {Promise<void>}
 */
export const refreshOpenedDraft = async (entryDraft) => {
  const draft = entryDraft.current;

  // A new entry has nothing to compare. A workflow draft isn’t refreshed here either: this check
  // watches the configured branch, while the draft lives on a branch of its own. That branch is
  // compared with the draft when it’s saved, by {@link detectWorkflowConflict}
  if (!draft || draft.isNew || !draft.originalEntry || isWorkflowDraft(draft)) {
    return;
  }

  try {
    await checkForRemoteChanges({ maxAge: MIN_CHECK_GAP });
  } catch (ex) {
    // eslint-disable-next-line no-console
    console.error('Failed to check the repository for changes.', ex);

    return;
  }

  if (!isDraftUntouched(entryDraft, draft)) {
    return;
  }

  const conflict = compareWithStore(draft.originalEntry);

  // An entry stored in a file with the other entries of the collection is told by its position, and
  // the item at the position may now be another entry that has moved there, which would silently
  // take the place of the entry being viewed
  if (conflict?.type !== 'modified' || !conflict.canOverwrite) {
    return;
  }

  const { collectionName, collection, collectionFile, expanderStates } = draft;

  // A backup from an earlier session is being offered, or about to be — the draft looks it up on
  // its own time, so the dialog can’t be relied upon to be showing yet. Restoring it into a draft
  // that has just been replaced would lose the restored edits, so the draft is left alone
  if (await getBackup(collectionName, getBackupSlug(draft))) {
    return;
  }

  // The user may have started on the draft, or moved on, while the backup was being looked up
  if (!isDraftUntouched(entryDraft, draft)) {
    return;
  }

  createDraft({
    entryDraft,
    collection,
    collectionFile,
    originalEntry: conflict.entry,
    expanderStates,
  });
};
